import React, { useEffect } from "react";
import HeroES from "@/components/landing-es/HeroES";
import SectionHeaderES from "@/components/landing-es/SectionHeaderES";
import PrimaryCardsES from "@/components/landing-es/PrimaryCardsES";
import PricingES from "@/components/landing-es/PricingES";
import PitchMeetingES from "@/components/landing-es/PitchMeetingES";
import FounderQuoteES from "@/components/landing-es/FounderQuoteES";
import SiteFooterES from "@/components/landing-es/SiteFooterES";
import { es } from "@/locales/es";
import registry from "@/locales/registry.json";

const SITE_ORIGIN = "https://voxiumscript.com";
// Path comes from the registry, not a hardcoded string — "/es/" with
// trailing slash, matching the /fr/ convention (see generate-fr-shell.js
// / generate-es-shell.js for why the trailing slash serves directly).
const ES_URL = `${SITE_ORIGIN}${registry.find((e) => e.code === "es").path}`;

/**
 * Spanish homepage — full mirrored route at /es. English homepage
 * (LandingPage.jsx) and the French homepage (LandingPageFR.jsx) are
 * untouched; this is a separate, additive page.
 *
 * Head management mirrors LandingPageFR.jsx exactly: mutate the existing
 * shell's <title> / canonical on mount, restore on unmount, extended to
 * <html lang>, meta description, and the JSON-LD script's
 * `description`/`url` fields. The JSON-LD `offers` array is read from
 * whatever is already in the DOM and left untouched, so Spanish offers
 * can never drift from the English ones.
 *
 * hreflang alternates are NOT touched here: they are static, identical
 * on every route (generated once into public/index.html from the locale
 * registry), so no runtime mutation is needed for them.
 */
const LandingPageES = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal:not(.is-visible)");
    if (!els.length || typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const prevLang = root.getAttribute("lang");
    root.setAttribute("lang", "es");

    const prevTitle = document.title;
    document.title = es.head.title;

    const descMeta = document.querySelector('meta[name="description"]');
    const prevDesc = descMeta ? descMeta.getAttribute("content") : null;
    if (descMeta) descMeta.setAttribute("content", es.head.description);

    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonical) canonical.setAttribute("href", ES_URL);

    // og:*/twitter:* content-swaps — same mutate-on-mount/restore-on-
    // unmount shape for each. og:image is deliberately excluded (brief:
    // "Do NOT localize the OG card image").
    const metaSwaps = [
      { selector: 'meta[property="og:title"]', value: es.head.title },
      { selector: 'meta[property="og:description"]', value: es.head.description },
      { selector: 'meta[property="og:url"]', value: ES_URL },
      { selector: 'meta[name="twitter:title"]', value: es.head.title },
      { selector: 'meta[name="twitter:description"]', value: es.head.description },
      { selector: 'meta[name="twitter:url"]', value: ES_URL },
    ].map(({ selector, value }) => {
      const el = document.querySelector(selector);
      const prevValue = el ? el.getAttribute("content") : null;
      if (el) el.setAttribute("content", value);
      return { el, prevValue };
    });

    // og:locale doesn't exist on the English shell at all — create it
    // for Spanish, remove it (not "restore to absent", literally remove
    // the node) on unmount so English never gains the tag.
    let ogLocale = document.querySelector('meta[property="og:locale"]');
    const ogLocaleExisted = !!ogLocale;
    if (!ogLocale) {
      ogLocale = document.createElement("meta");
      ogLocale.setAttribute("property", "og:locale");
      document.head.appendChild(ogLocale);
    }
    const prevOgLocale = ogLocaleExisted ? ogLocale.getAttribute("content") : null;
    ogLocale.setAttribute("content", "es_ES");

    const jsonLdScript = document.querySelector('script[type="application/ld+json"]');
    const prevJsonLd = jsonLdScript ? jsonLdScript.textContent : null;
    if (jsonLdScript) {
      try {
        const data = JSON.parse(jsonLdScript.textContent);
        data.description = es.head.description;
        data.url = ES_URL;
        jsonLdScript.textContent = JSON.stringify(data, null, 2);
      } catch {
        // Malformed JSON-LD in the shell — leave it untouched rather
        // than break the page over a non-critical metadata block.
      }
    }

    return () => {
      if (prevLang !== null) root.setAttribute("lang", prevLang);
      else root.removeAttribute("lang");
      document.title = prevTitle;
      if (descMeta && prevDesc !== null) descMeta.setAttribute("content", prevDesc);
      if (canonical && prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
      metaSwaps.forEach(({ el, prevValue }) => {
        if (el && prevValue !== null) el.setAttribute("content", prevValue);
      });
      if (ogLocaleExisted) {
        if (prevOgLocale !== null) ogLocale.setAttribute("content", prevOgLocale);
      } else {
        ogLocale.remove();
      }
      if (jsonLdScript && prevJsonLd !== null) jsonLdScript.textContent = prevJsonLd;
    };
  }, []);

  return (
    <main data-testid="landing-page-es" className="bg-cream" lang="es">
      <HeroES />
      <SectionHeaderES />
      <PrimaryCardsES />
      <PricingES />
      <PitchMeetingES />
      <FounderQuoteES />
      <SiteFooterES />
    </main>
  );
};

export default LandingPageES;
